import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { BehaviorSubject, Subscription, debounceTime } from 'rxjs';
import { PagedTenantResponse, Tenant, TenantApi } from 'src/app/hickory-api';
import { ApiError } from '../../model/utility';

class TreeNode {
  level: number;
  key: string;
  name: string;
  expandable: boolean;
  children: TreeNode[];

  source?: Tenant;
  count?: number;
  checked?: boolean;
  selected?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;

  get parentId(): string { return this.source?.brandName; }

  constructor(key: string, source?: Tenant) {
    this.source = source;
    this.key = key;
    this.name = source?.name ?? key;
    this.level = 1;
    this.children = [];
    this.expandable = false;
  }

  addChild(node: TreeNode) {
    this.level = 0;
    this.expandable = true;
    this.children.push(node);
    this.count = this.children.length;
  }
}

@Component({
  selector: 'app-tenant-selection',
  templateUrl: './tenant-selection.component.html',
  styleUrls: ['./tenant-selection.component.scss'],
  standalone: false
})
export class TenantSelectionComponent implements OnInit, OnDestroy {
  @Input() selectionsIn: BehaviorSubject<Tenant[]>;
  @Output() selectionsOut = new EventEmitter<Tenant[]>();

  isError: boolean = false; // indicates that we encountered an error fetching the tenants
  inProgress: boolean = true; // indicates that we're fetching nodes

  // cleanup subscriptions on destroy
  subscription: Subscription = new Subscription();

  selectedItems: TreeNode[] = [];
  selectedTenants: Tenant[] = [];
  get selectedTotal(): number { return this.selectedTenants.length; }

  // search
  searchControl = new UntypedFormControl();
  searchQuery: string;
  searchResults: TreeNode[];
  searchInProgress: boolean = false;

  // paste tenant IDs functionality - allows users to paste a list of tenant IDs for bulk selection
  pasteControl = new UntypedFormControl();

  // tree view
  treeControl = new FlatTreeControl<TreeNode>(node => node.level, node => node.expandable);
  treeFlattener = new MatTreeFlattener((node: TreeNode, level: number) => {
    node.level = level;
    return node;
  }, node => node.level, node => node.expandable, node => node.children);
  treeDataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  treeDataUpdates = new BehaviorSubject<void>(null);

  // data
  // key: Tenant.id OR brand name -- cache of all nodes
  private dataMap = new Map<string, TreeNode>();
  private brands: string[] = [];

  productControl = new UntypedFormControl();
  productEditions: string[] = [];
  productFilter: string = undefined;

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private tenantApi: TenantApi,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.fetchAll();

    // listen for updates to selections from the parent component
    if (this.selectionsIn) this.subscription.add(this.selectionsIn.asObservable().subscribe(selections => this.updateSelectionsFromInput(selections)));

    // debounce group search and trigger the refresh
    this.subscription.add(this.searchControl.valueChanges.pipe(debounceTime(200)).subscribe(newValue => this.onSearch(newValue)));

    // listen for additional tree data changes
    this.subscription.add(this.treeDataUpdates.asObservable().pipe(debounceTime(100)).subscribe(_ => this.updateTreeDataSource()));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  fetchAll(page: number = 1, pageSize: number = 200) {
    if (page == 1) this.resetData();
    this.inProgress = true;
    this.isError = false;
    this.subscription.add(this.tenantApi.getTenants(null, null, null, null, this.productFilter, false, page, pageSize).subscribe({
      next: (res) => this.processFetchResponse(res, null),
      error: (err: ApiError) => this.processFetchResponse(null, err),
    }));
  }

  private processFetchResponse(res: PagedTenantResponse, err: ApiError) {
    if (err) {
      this.inProgress = false;
      this.isError = true;
      return;
    }

    if (!res) return;

    res?.items?.forEach(tenant => this.addTenant(tenant));

    // sort tenants as children on brands
    this.getBrandNodes().forEach(r => r.children = [...r.children].sort((a, b) => a.name.localeCompare(b.name)));
    this.updateTreeDataSource();

    if (res?.pageCount > res?.page) {
      this.fetchAll(res?.page + 1);
    } else {
      this.inProgress = false;
    }
  }

  private addTenant(tenant: Tenant): TreeNode {
    if (!!tenant.productEdition?.length && !this.productEditions.includes(tenant.productEdition)) { 
      this.productEditions.push(tenant.productEdition); 
      this.productEditions.sort((a, b) => a.localeCompare(b));
    }
    const tenantKey = '' + tenant.id;
    const tenantNode = this.dataMap.get(tenantKey) ?? new TreeNode(tenantKey, tenant);
    this.dataMap.set(tenantKey, tenantNode);

    const brandKey = tenant.brandName ?? 'Undetermined';
    const brandNode = this.dataMap.get(brandKey) ?? new TreeNode(brandKey);
    brandNode.addChild(tenantNode);
    if (!this.brands.includes(brandKey)) this.brands.push(brandKey);
    this.dataMap.set(brandNode.key, brandNode);
    return tenantNode;
  }

  onSearch(newValue: string) {
    const query = newValue || null;
    this.searchQuery = query;
    if (query) {
      const regex = new RegExp(query, 'i');
      this.searchResults = [...this.dataMap.values()]
        .filter(n => !!n.source && regex.test(n.name))
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 25);
    } else {
      this.onResetSearch();
    }
  }

  onResetSearch() {
    this.searchControl.reset();
    this.searchQuery = null;
    this.searchResults = null;
  }

  onProductFilterChange(value: string) {
    this.productFilter = value;
    this.fetchAll();
  }

  onAutoComplete(node: TreeNode) {
    this.onSelectedChange(node, true);
  }

  onSelectedChange(node: TreeNode, selected: boolean) {
    node.checked = selected;
    const items = this.selectedItems.filter(n => n.key !== node.key);
    if (selected) items.push(node);
    this.selectedItems = items;
    this.selectedTenants = this.getSelectedTenants();
    this.selectionsOut.emit(this.selectedTenants);
    this.treeDataUpdates.next();
  }

  onChipRemove(node: TreeNode) {
    this.onSelectedChange(node, false);
  }

  onPasteTenantIds() {
    const pastedValue = this.pasteControl.value;
    if (!pastedValue || !pastedValue.trim()) {
      return;
    }

    // Parse the pasted content - support comma, semicolon, newline, or space separation
    const rawIds = pastedValue
      .split(/[,;\n\r\s]+/)
      .map((id: string) => id.trim())
      .filter((id: string) => id.length > 0);

    const matchedCount = this.selectTenantsByIds(rawIds);
    const notFoundIds = rawIds.filter(id => !this.dataMap.has(id));

    // Show toast message with results
    let message = `${matchedCount} tenant(s) selected`;
    if (notFoundIds.length > 0) {
      message += `. ${notFoundIds.length} ID(s) not found: ${notFoundIds.join(', ')}`;
    }

    const config = {
      duration: notFoundIds.length > 0 ? 5000 : 3000,
      panelClass: notFoundIds.length > 0 ? 'snackbar-warn-message' : 'snackbar-status-message'
    };

    this.snackBar.open(message, 'OK', config);

    // Clear the paste input after processing
    this.pasteControl.setValue('');
  }

  private selectTenantsByIds(tenantIds: string[]): number {
    let matchedCount = 0;
    
    tenantIds.forEach(id => {
      const node = this.dataMap.get(id);
      if (node && node.source && !this.isSelected(node.key)) {
        this.onSelectedChange(node, true);
        matchedCount++;
      }
    });

    return matchedCount;
  }

  private resetData() {
    this.brands = [];
    this.selectedItems = [];
    this.selectedTenants = [];
    this.dataMap.clear();
  }

  private updateTreeDataSource() {
    this.dataMap.forEach(n => this.updateNodeState(n));
    const roots = this.getBrandNodes().sort((a, b) => a.name.localeCompare(b.name));
    roots.forEach(r => r.children = [...r.children].sort((a, b) => a.name.localeCompare(b.name)));
    this.treeDataSource.data = roots;
  }

  private updateSelectionsFromInput(selections: Tenant[]) {
    this.selectedItems = selections?.map?.(t => this.addTenant(t));
    this.selectedTenants = this.getSelectedTenants();
    this.treeDataUpdates.next();
  }

  private getSelectedTenants(): Tenant[] {
    const nodes: TreeNode[] = [];
    const addDistinct = function(n: TreeNode) {
      if (!!n.source && !nodes.find(x => n.key === x.key)) nodes.push(n);
      n.children?.forEach(c => addDistinct(c));
    }

    this.selectedItems.forEach(n => addDistinct(n));
    return nodes.flatMap(f => f.source ? [f.source] : []);
  }

  private getBrandNodes(): TreeNode[] {
    const nodes: TreeNode[] = [];
    this.brands.forEach(b => {
      const node = this.dataMap.get(b);
      if (node) nodes.push(node);
    });

    return nodes;
  }

  private updateNodeState(node: TreeNode) {
    node.checked = this.isChecked(node.key);
    node.selected = this.isSelected(node.key);
    node.indeterminate = this.isChildSelected(node.key);
    node.disabled = this.isDisabled(node.key);
  }

  private isSelected(id: string): boolean {
    if (!id) return false;
    return !!this.selectedItems.find(n => n.key === id);
  }

  private isChecked(id: string): boolean {
    if (!id) return false;
    return this.isSelected(id) || this.isParentSelected(id);
  }

  private isParentSelected(id: string): boolean {
    if (!id) return false;
    const parentId = this.dataMap.get(id)?.parentId
    if (!parentId) return false;
    if (id === parentId) return this.isSelected(id);
    return this.isSelected(parentId) || this.isParentSelected(parentId);
  }

  private isDisabled(id: string): boolean {
    return this.isParentSelected(id);
  }

  private isChildSelected(id: string): boolean {
    if (!id) return false;
    const item = this.dataMap.get(id);
    if (!item) return false;
    if (this.isChecked(id) || !item?.children?.length) {
      return false;
    } else {
      return !!item?.children?.find(n => this.isSelected(n.key));
    }
  }
}
